import type { GeoReading } from '@tradeoutlet/shared';

export type GeoFailure = 'unsupported' | 'denied' | 'unavailable' | 'timeout';

/** One location reading for a clock event. Resolves null (with the reason) rather than throwing. */
/** `fresh`: ignore a cached reading (Try again after moving). */
export function readPosition(timeoutMs = 10_000, fresh = false): Promise<{ reading: GeoReading | null; failure?: GeoFailure }> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve({ reading: null, failure: 'unsupported' });
  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      p => resolve({ reading: { lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: Math.round(p.coords.accuracy) } }),
      e => resolve({ reading: null, failure: e.code === e.PERMISSION_DENIED ? 'denied' : e.code === e.TIMEOUT ? 'timeout' : 'unavailable' }),
      { enableHighAccuracy: true, maximumAge: fresh ? 0 : 30_000, timeout: timeoutMs },
    );
  });
}

export const GEO_FAILURE_TEXT: Record<GeoFailure, string> = {
  unsupported: 'This browser can\'t share your location.',
  denied: 'Location access is blocked for this site. Allow it in your browser settings, or use the kiosk.',
  unavailable: 'Your location isn\'t available right now.',
  timeout: 'Finding your location took too long.',
};

/** t182u: a reading only when the site already has location permission, so nothing prompts just for a default. */
export async function readPositionIfAllowed(timeoutMs = 5_000): Promise<GeoReading | null> {
  try {
    const st = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    if (st?.state !== 'granted') return null;
  } catch { return null; }
  return (await readPosition(timeoutMs)).reading;
}
